import { useEffect, useRef, useCallback, useState } from 'react'
import { supabase } from '@/lib/supabase'

interface UseAutoSaveOptions {
  projectRequestId: string | null
  data: Record<string, unknown>
  interval?: number // milliseconds
  disabled?: boolean
  localStorageKey?: string
}

interface UseAutoSaveReturn {
  lastSaved: Date | null
  isSaving: boolean
  error: Error | null
  saveImmediate: () => Promise<void>
}

export function useAutoSave({
  projectRequestId,
  data,
  interval = 30000, // 30 secondes par défaut
  disabled = false,
  localStorageKey = 'project_draft_data',
}: UseAutoSaveOptions): UseAutoSaveReturn {
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const previousDataRef = useRef<string>('')
  const intervalRef = useRef<NodeJS.Timeout>()

  // Fonction de sauvegarde
  const save = useCallback(async () => {
    if (disabled || !data) return

    const currentDataStr = JSON.stringify(data)

    // Ne pas sauvegarder si les données n'ont pas changé
    if (currentDataStr === previousDataRef.current) {
      return
    }

    setIsSaving(true)
    setError(null)

    try {
      // 1. Sauvegarde dans localStorage (synchrone, toujours réussie)
      localStorage.setItem(localStorageKey, currentDataStr)

      // 2. Sauvegarde dans Supabase (si ID de brouillon disponible)
      if (projectRequestId) {
        const { error: supabaseError } = await supabase
          .from('client_requests')
          .update({
            data,
            last_edited_at: new Date().toISOString(),
            is_saved_draft: true,
          })
          .eq('id', projectRequestId)

        if (supabaseError) {
          throw new Error(`Erreur Supabase: ${supabaseError.message}`)
        }
      }

      previousDataRef.current = currentDataStr
      setLastSaved(new Date())
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Erreur inconnue')
      setError(errorObj)
      console.error('Erreur auto-save:', errorObj)
    } finally {
      setIsSaving(false)
    }
  }, [data, disabled, projectRequestId, localStorageKey])

  // Sauvegarde immédiate (appelée au blur ou sur demande)
  const saveImmediate = useCallback(async () => {
    await save()
  }, [save])

  // Auto-save périodique
  useEffect(() => {
    if (disabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
      return
    }

    // Première sauvegarde immédiate au montage
    save()

    // Puis sauvegarde périodique
    intervalRef.current = setInterval(save, interval)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [save, interval, disabled])

  // Warning avant fermeture si changements non sauvegardés
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const currentDataStr = JSON.stringify(data)
      if (currentDataStr !== previousDataRef.current && !disabled) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [data, disabled])

  return {
    lastSaved,
    isSaving,
    error,
    saveImmediate,
  }
}
